import React from 'react';
import { Chip, ChipProps, Box, alpha } from '@mui/material';
import { getJurisdictionColor } from '../../utils/jurisdictionColors';

interface JurisdictionBadgeProps {
  jurisdiction: string | null | undefined;
  size?: ChipProps['size'];
  variant?: 'filled' | 'outlined' | 'dot';
  showCount?: boolean;
  count?: number;
  onClick?: () => void;
}

/**
 * A reusable component for displaying jurisdiction names with their associated flag colors.
 * Provides consistent jurisdiction visualization across the application.
 */
const JurisdictionBadge: React.FC<JurisdictionBadgeProps> = ({
  jurisdiction,
  size = 'small',
  variant = 'filled',
  showCount = false,
  count,
  onClick,
}) => {
  if (!jurisdiction) {
    return (
      <Chip
        label="Unknown"
        size={size}
        sx={{ bgcolor: '#f5f5f5', color: '#666' }}
      />
    );
  }

  const color = getJurisdictionColor(jurisdiction);
  const label = showCount && count !== undefined
    ? `${jurisdiction} (${count})`
    : jurisdiction;

  if (variant === 'dot') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { opacity: 0.8 } : {},
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Box
          component="span"
          sx={{
            fontSize: size === 'small' ? '0.75rem' : '0.875rem',
            color: 'text.primary',
          }}
        >
          {label}
        </Box>
      </Box>
    );
  }

  if (variant === 'outlined') {
    return (
      <Chip
        label={label}
        size={size}
        onClick={onClick}
        sx={{
          borderColor: color,
          color: color,
          bgcolor: 'transparent',
          fontWeight: 500,
          '&:hover': onClick ? {
            bgcolor: alpha(color, 0.08),
          } : {},
        }}
        variant="outlined"
      />
    );
  }

  // Default: filled variant
  return (
    <Chip
      label={label}
      size={size}
      onClick={onClick}
      sx={{
        bgcolor: color,
        color: '#fff',
        fontWeight: 500,
        '&:hover': onClick ? {
          bgcolor: color,
          opacity: 0.9,
        } : {},
      }}
    />
  );
};

export default JurisdictionBadge;
